import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { Stars03 } from '@kula-ai/icons-core';

interface NoteTakerConfigProps {
  config?: Record<string, unknown>;
  fieldProps?: Record<string, unknown>;
  /**
   * Force mandatory on when the org setting can't be read from
   * `atsJobCreate.currentJob` — e.g. the settings interview-plan flow,
   * where mandatory comes from the template's `note_taker_mandatory`.
   */
  mandatoryOverride?: boolean;
}

const FIELD_NAME = 'enable_note_taker';
const NOTE_TAKER_LABEL = 'Enable AI notetaker?';
const NOTE_TAKER_DESCRIPTION =
  'The AI Notetaker automatically captures key points and summaries during meetings. It organizes and highlights information for easy review and follow-up.';
const NOTE_TAKER_DISABLED_TOOLTIP =
  'AI note taker is required for this role as configured by the admin.';

export const useNoteTakerConfig = (props: NoteTakerConfigProps = {}) => {
  const { currentJob = {} } = useSelector(getAtsJobCreate, shallowEqual);

  const isNoteTakerMandatory =
    (currentJob?.data?.note_taker?.mandatory ?? false) ||
    Boolean(props.mandatoryOverride);

  const field = useMemo(
    () => ({
      type: 'switch' as const,
      name: FIELD_NAME,
      required: isNoteTakerMandatory,
      condition: {
        operator: 'in',
        field: 'location',
        value: ['zoom', 'google_meet', 'teams'],
      },
      classNames: {
        // Figma: 12px (p-3), no border. Background = secondary-25 (#F8F9FC).
        container: 'bg-secondary-25 p-3 rounded-md',
      },
      disabled: isNoteTakerMandatory,
      ...(props.config || {}),
      fieldProps: {
        switchLabel: NOTE_TAKER_LABEL,
        description: NOTE_TAKER_DESCRIPTION,
        labelLeftElement: <Stars03 size={18} className="!text-primary-500" />,
        // Figma label = primary-500 / 14px / weight 550.
        labelClassName: '!text-primary-500 !font-[550]',
        // Figma description = 14px / 150% / weight 450 / secondary-700.
        descriptionClassName:
          '!text-sm !leading-[150%] !font-[450] !text-secondary-700',
        labelAlign: 'center',
        fullWidth: true,
        labelPosition: 'left',
        // Native (1.10.78): tooltip shown only when the toggle is
        // disabled, on hover/focus of the switch itself. Set only when
        // mandatory so the enabled state stays clean.
        // `dimLabelWhenDisabled: false` keeps the label + description
        // at full Figma colors while the toggle is locked — only the
        // switch itself reads as disabled.
        ...(isNoteTakerMandatory
          ? {
              disabledTooltip: NOTE_TAKER_DISABLED_TOOLTIP,
              dimLabelWhenDisabled: false,
            }
          : {}),
        ...(props.fieldProps || {}),
      },
    }),
    [props, isNoteTakerMandatory]
  );

  return { field, isMandatory: isNoteTakerMandatory };
};
